import { useEffect } from "react";

import { toast } from "sonner";

// The donor's `ItemsError` — the one snackbar every item surface reports a
// failure through.
//
// Source: `timmy/src/domains/items/shared/components/ItemsError.tsx`. It is a
// `Notification` fixed at `severity='error'`, anchored bottom-centre, bold and
// `mb: 4` (32px) clear of the edge, auto-hiding after ten seconds unless the
// caller says otherwise:
//
// ```
// <Notification open severity='error'
//   anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
//   autoHideDuration={autoHideDuration}   // default 10000
//   onClose={onClose}
//   sx={{ mb: 4, fontWeight: 'bold' }}>
// ```
//
// **Shared rather than page-local** (`docs/agents/migration-guidelines.md`,
// "Shared donor components port as shared Atlas components"): the donor's item
// domain has exactly one of these and four surfaces render it — the details
// page's three failures, the Request Item dialog's two, and the Add Variant
// dialog's one. Copied per screen, the bold and the 32px were already missing
// from two of them.
//
// A hook rather than a component because this app's snackbars are sonner toasts,
// which are raised imperatively. `position` is set per toast rather than on the
// `Toaster`: the app's outlet defaults to top-centre and is shared with surfaces
// that are nobody's port (the sign-out failure), so asking for the donor's
// corner here is parity for the screens that have a donor and leaves the default
// alone for the screens that do not.

/** The donor's `autoHideDuration = 10000`. */
export const ITEMS_ERROR_DURATION_MS = 10_000;

export interface ItemsErrorOptions {
  /** The donor's `onClose` — dismissal *and* auto-close, which is what MUI's
   * single `onClose` covers. */
  onClose?: () => void;
  /**
   * The donor's `autoHideDuration`. `Infinity` is its `null`: the Request Item
   * dialog's image failure stays up because there is something to *do* about it
   * (remove the image), where a refused submit is only news.
   *
   * **MUI's timer only fires through `onClose`**, so in the donor a snackbar
   * with no `onClose` never closes however short its duration reads — see the
   * details pages' load error, which passes none and is therefore permanent
   * (`ItemDetailsIsland.tsx:192-194`). That rule is *not* encoded here as a
   * default, because Atlas's dialog call sites omit `onClose` where the donor
   * supplies one (`resetError`) and so should keep their 10s; the two call
   * sites that need it ask for `Infinity` explicitly and say why.
   */
  duration?: number;
}

/**
 * One `ItemsError` snackbar: mounted for exactly as long as `active` holds, at
 * the donor's corner, weight, inset and duration.
 *
 * The mounting is the point. The donor writes `{cond && <ItemsError …/>}`, so a
 * failure that is no longer true has no snackbar; the cleanup below is that
 * conditional render, expressed against an imperative toaster.
 */
export function useItemsError(active: boolean, message: string, options: ItemsErrorOptions = {}) {
  const { onClose, duration = ITEMS_ERROR_DURATION_MS } = options;

  useEffect(() => {
    if (!active) return;
    const id = toast.error(message, {
      duration,
      position: "bottom-center",
      // `ItemsError.tsx:14` — 32px clear of the bottom edge.
      style: { marginBottom: "32px" },
      // …and bold. The weight has to land on the *title* element, with
      // `!important`, rather than being inherited from the toast root: sonner
      // ships `[data-sonner-toast][data-styled='true'] [data-title] {
      // font-weight: 500 }` (`sonner/dist/styles.css:141`), and that
      // declaration's own specificity (0,3,0) beats both an inherited value
      // from the root and a bare `font-bold` utility. The donor gets it for
      // free — `sx` lands on its MUI `Alert` root and MUI declares no weight on
      // `.MuiAlert-message`, so the bold inherits.
      //
      // **As a descendant variant on the toast, not as `classNames.title`.**
      // Since #409 the outlet states the donor's `body2` weight on every
      // message (`sonner.tsx`, `TOAST_MESSAGE`), and sonner merges that class
      // and this one onto the same element — so `font-bold!` beside
      // `font-normal!` would be two `!important` declarations at the same
      // (0,1,0) specificity, decided by whichever Tailwind happens to emit
      // last. Written this way the selector is `.…\:font-bold\! [data-title]`
      // at (0,2,0) and wins on specificity, whatever the source order.
      // `sonner.layout.browser.test.tsx` pins it.
      classNames: { toast: "[&_[data-title]]:font-bold!" },
      // No swipe-away. The donor's snackbar is deliberately hard to lose:
      // `Notification`'s own close handler returns early on `clickaway` and
      // `escapeKeyDown` — "the notification is a semi persistent component. To
      // that end, we don't want to accidentally close it"
      // (`react-ui/src/notification/notification.tsx:59-64`) — and `ItemsError`
      // passes no `closable`, so there is no X either
      // (`react-ui/src/alert/alert.tsx:58`). The timer is the only way out.
      // Sonner's `dismissible` defaults true, which adds a drag-to-fling the
      // source does not have.
      //
      // Which leaves the timer as the only way out — and only where the caller
      // gave an `onClose` for MUI's timer to fire through. See `duration`.
      dismissible: false,
      onDismiss: onClose,
      onAutoClose: onClose,
    });
    return () => {
      toast.dismiss(id);
    };
    // `onClose` is a dismissal callback, not part of the condition: including it
    // would tear down and re-raise the toast on every render of an unstable
    // caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, message, duration]);
}

/**
 * `ItemDeleteIsland`'s success snackbar (`ItemDeleteIsland.tsx:67-74`) —
 * top-centre, five seconds — raised by both hosts of a delete: the item page
 * route, and the Item Search island's item dialog (#798).
 */
export function announceItemDeleted(itemId: number) {
  toast.success(`The Item ${itemId} has been successfully deleted.`, {
    duration: 5_000,
    // `sx={{ mt: 17 }}` — 136px below the Snackbar's own 24px top
    // anchor, which clears the app header. The `sx` lands on the
    // *Alert*, not the Snackbar root: `Notification` splits them
    // and forwards `rootProps.sx` to the Snackbar while `sx` goes
    // to the child (`react-ui/src/notification/notification.tsx:
    // 79-101`), so this is a margin on the panel rather than a
    // different anchor. The failure snackbar's mirror image of
    // this — `mb: 4` → 32px — is carried in `items-error.ts`.
    style: { marginTop: "136px" },
    // Not swipeable, for the reason spelled out in
    // `items-error.ts`: the donor's snackbar ignores clickaway and
    // Escape and offers no X, so its timer is the only way out.
    dismissible: false,
  });
}
